import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { getCheckoutStatus } from "../lib/api";
import { useCart } from "../context/CartContext";

const Success = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ state: "checking", payment_status: null });
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setStatus({ state: "error", message: "Missing session id" });
      return;
    }

    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const data = await getCheckoutStatus(sessionId);
        if (data.payment_status === "paid") {
          setStatus({ state: "paid", payment_status: "paid", amount: data.amount_total / 100 });
          clear();
          return;
        }
        if (data.status === "expired") {
          setStatus({ state: "expired" });
          return;
        }
        attempts += 1;
        if (attempts >= 8) {
          setStatus({ state: "pending" });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setStatus({ state: "error", message: e?.response?.data?.detail || e.message });
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, clear]);

  return (
    <div data-testid="success-page" className="pt-32 pb-24 min-h-[80vh] flex items-start justify-center">
      <div className="max-w-xl w-full px-6 md:px-12 text-center">
        <div className="mx-auto inline-flex items-center justify-center w-16 h-16 border hairline mb-10">
          {status.state === "paid" ? (
            <Check size={28} className="gold-text" strokeWidth={1.2} />
          ) : (
            <img src="/mountain-mark.png" alt="" aria-hidden className="h-6 w-auto select-none" draggable={false} />
          )}
        </div>

        {status.state === "checking" && (
          <>
            <h1 className="font-serif text-4xl md:text-5xl">Confirming your order…</h1>
            <p className="mt-4 text-[10px] uppercase tracking-label text-[var(--secondary-text)]">
              Please remain on this page.
            </p>
          </>
        )}

        {status.state === "paid" && (
          <>
            <h1 className="font-serif text-4xl md:text-5xl">Thank you.</h1>
            <div className="divider-gold mx-auto mt-6 mb-6" />
            <p className="text-[var(--secondary-text)] leading-loose">
              Your order has been received. A confirmation message has been sent to your inbox.
              Each bottle is prepared for you before it leaves our shop.
            </p>
            {status.amount && (
              <p className="mt-6 font-serif text-2xl gold-text" data-testid="success-amount">
                ${Number(status.amount).toFixed(2)}
              </p>
            )}
          </>
        )}

        {status.state === "expired" && (
          <>
            <h1 className="font-serif text-4xl md:text-5xl">Session expired</h1>
            <p className="mt-4 text-[var(--secondary-text)]">Please return to your cart and try again.</p>
          </>
        )}

        {status.state === "pending" && (
          <>
            <h1 className="font-serif text-4xl md:text-5xl">Still processing</h1>
            <p className="mt-4 text-[var(--secondary-text)]">
              Your payment is taking longer than expected. You will receive an email once confirmed.
            </p>
          </>
        )}

        {status.state === "error" && (
          <>
            <h1 className="font-serif text-4xl md:text-5xl">Something went wrong</h1>
            <p className="mt-4 text-[var(--secondary-text)]">{status.message}</p>
          </>
        )}

        <div className="mt-12 flex items-center justify-center gap-4">
          <Link to="/" className="btn-secondary" data-testid="success-home-btn">
            Return Home
          </Link>
          <Link to="/shop" className="btn-secondary inline-flex items-center gap-3" data-testid="success-shop-btn">
            Continue Shopping <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Success;
